import { useForm, useList } from "@refinedev/core";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { FormStickyActions, FormHeader, FormSpacer } from "@/components/forms";
import { FileImageUpload } from "@/components/upload/FileImageUpload";
import {
  TranslationsForm,
  LanguageSwitcher,
  createEmptyTranslationData,
  type TranslationData,
  type TranslationFieldConfig,
  type TranslationLanguage,
  type LanguageCode,
} from "@/components/translations";
import { Plus, Trash2, Link as LinkLucide } from "lucide-react";
import { detectPlatformFromUrl, urlsToSocialLinks, type SocialPlatform } from "@/lib/social-links";
import {
  TelegramIcon,
  InstagramIcon,
  LinkedinIcon,
  XTwitterIcon,
  YoutubeIcon,
  TiktokIcon,
  FacebookIcon,
  LinkIcon,
} from "@/components/icons/social-icons";
import type { Teacher, TeacherFormData } from "./types";

const PLATFORM_ICONS: Record<SocialPlatform, React.ComponentType<{ className?: string }>> = {
  telegram: TelegramIcon,
  instagram: InstagramIcon,
  twitter: XTwitterIcon,
  linkedin: LinkedinIcon,
  youtube: YoutubeIcon,
  tiktok: TiktokIcon,
  facebook: FacebookIcon,
  website: LinkIcon,
};

const FIELDS: TranslationFieldConfig[] = [
  { name: "name", label: "Имя", type: "input", required: true },
  { name: "role", label: "Роль / Должность", type: "input" },
  { name: "bio", label: "Биография", type: "richtext" },
];

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
};

/**
 * TeachersForm
 *
 * Форма управления учителями
 *
 * @returns {React.ReactElement} React компонент
 */
export function TeachersForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { formLoading, onFinish, query } = useForm<Teacher>({
    resource: "teachers",
    action: isEdit ? "edit" : "create",
    id,
    redirect: "list",
  });

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: "languages",
  });

  const languages: TranslationLanguage[] | undefined =
    languagesResult?.data?.map((l) => ({
      code: l.code,
      name: l.name,
      nativeName: l.nativeName,
      flag: l.flag || l.flagEmoji || "",
    }));

  const teacher = query?.data?.data;
  const initialState = buildTeacherFormState(teacher);
  const formKey = isEdit ? `edit-${teacher?.id ?? "loading"}` : "create";

  return (
    <TeachersFormFields
      key={formKey}
      isEdit={isEdit}
      formLoading={formLoading}
      initialState={initialState}
      order={teacher?.order ?? 0}
      onFinish={async (values) => {
        await onFinish(values);
      }}
      onBack={() => navigate(-1)}
      languages={languages}
    />
  );
}

interface FileRecord {
  id: number;
  url: string;
}

interface TeacherFormState {
  photo: FileRecord | null;
  isActive: boolean;
  socialLinks: string[];
  translations: TranslationData;
}

function buildTeacherFormState(teacher?: Teacher): TeacherFormState {
  const baseState: TeacherFormState = {
    photo: null,
    isActive: true,
    socialLinks: [],
    translations: createEmptyTranslationData(),
  };

  if (!teacher) {
    return baseState;
  }

  const translations = createEmptyTranslationData();
  for (const t of teacher.translations ?? []) {
    translations[t.languageCode as keyof TranslationData] = {
      name: t.name ?? "",
      role: t.role ?? "",
      bio: t.bio ?? "",
    };
  }

  return {
    ...baseState,
    photo: teacher.photoId && teacher.photoUrl
      ? { id: teacher.photoId, url: teacher.photoUrl }
      : teacher.photoUrl ? { id: 0, url: teacher.photoUrl } : null,
    isActive: teacher.isActive,
    socialLinks: Object.values(teacher.socialLinks ?? {}),
    translations,
  };
}

interface TeachersFormFieldsProps {
  isEdit: boolean;
  formLoading: boolean;
  initialState: TeacherFormState;
  order: number;
  onFinish: (values: TeacherFormData) => Promise<void>;
  onBack: () => void;
  languages?: TranslationLanguage[];
}

function TeachersFormFields({
  isEdit,
  formLoading,
  initialState,
  order,
  onFinish,
  onBack,
  languages,
}: TeachersFormFieldsProps) {
  const [photo, setPhoto] = useState<FileRecord | null>(initialState.photo);
  const [isActive, setIsActive] = useState(initialState.isActive);
  const [socialLinks, setSocialLinks] = useState(initialState.socialLinks);
  const [translations, setTranslations] = useState<TranslationData>(
    initialState.translations
  );
  const [activeLanguage, setActiveLanguage] = useState<LanguageCode>('ru');

  const addSocialLink = () =>
    setSocialLinks([...socialLinks, ""]);
  const removeSocialLink = (index: number) =>
    setSocialLinks(socialLinks.filter((_, i) => i !== index));
  const updateSocialLink = (index: number, val: string) => {
    const updated = [...socialLinks];
    updated[index] = val;
    setSocialLinks(updated);
  };

  const handleFormSubmit = async () => {
    const nonEmptyLinks = socialLinks.filter((url) => url.trim() !== '');

    const formData: TeacherFormData = {
      photoUrl: photo?.url ?? null,
      photoId: photo?.id ?? null,
      socialLinks: urlsToSocialLinks(nonEmptyLinks),
      isActive,
      order,
      translations: (
        languages?.map((l) => l.code) ||
        Object.keys(translations).filter((k) => translations[k]?.name)
      )
        .map((code) => ({
          languageCode: code,
          name: translations[code]?.name ?? "",
          role: translations[code]?.role || null,
          bio: translations[code]?.bio || null,
        }))
        .filter((t) => t.name),
    };
    await onFinish(formData);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit();
      }}
      className="space-y-6"
    >
      <FormHeader
        title={isEdit ? "Редактировать учителя" : "Добавить учителя"}
        onBack={onBack}
        sticky
        actions={
          languages && languages.length > 0 ? (
            <LanguageSwitcher
              languages={languages}
              activeLanguage={activeLanguage}
              onLanguageChange={(code) => setActiveLanguage(code as LanguageCode)}
              fields={FIELDS}
              value={translations}
              variant="compact"
            />
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Информация</CardTitle>
            </CardHeader>
            <CardContent>
              <TranslationsForm
                fields={FIELDS}
                value={translations}
                onChange={setTranslations}
                languages={languages}
                activeLanguage={activeLanguage}
                onLanguageChange={(code) => setActiveLanguage(code as LanguageCode)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Социальные сети</CardTitle>
              <Button variant="outline" size="sm" type="button" onClick={addSocialLink}>
                <Plus className="h-4 w-4 mr-1" />
                Добавить
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {socialLinks.length === 0 ? (
                <p className="text-muted-foreground text-sm">Нет ссылок</p>
              ) : (
                socialLinks.map((url, index) => (
                  <SocialLinkRow
                    key={index}
                    url={url}
                    onChange={(val) => updateSocialLink(index, val)}
                    onRemove={() => removeSocialLink(index)}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Фото</CardTitle>
            </CardHeader>
            <CardContent>
              <FileImageUpload value={photo} onChange={setPhoto} aspectRatio="1/1" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Настройки</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={isActive}
                  onCheckedChange={(c) => setIsActive(c === true)}
                />
                <label htmlFor="isActive" className="text-sm font-medium">
                  Активен
                </label>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <FormSpacer />

      <FormStickyActions
        primaryLabel={isEdit ? "Сохранить" : "Создать"}
        secondaryLabel="Отмена"
        onSecondary={onBack}
        isPending={formLoading}
      />
    </form>
  );
}

interface SocialLinkRowProps {
  url: string;
  onChange: (val: string) => void;
  onRemove: () => void;
}

function SocialLinkRow({ url, onChange, onRemove }: SocialLinkRowProps) {
  const platform = detectPlatformFromUrl(url);
  const PlatformIcon = PLATFORM_ICONS[platform];

  return (
    <div className="flex gap-2 items-center">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted/50">
        {url.trim() ? (
          <PlatformIcon className="h-4 w-4 text-foreground" />
        ) : (
          <LinkLucide className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <Input
        placeholder="https://t.me/username, instagram.com/user..."
        value={url}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1"
      />
      <Button
        variant="ghost"
        size="icon"
        type="button"
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>
    </div>
  );
}
